// Generated by Claude Code
import { PublicWorkOrderState } from '#components/domain/operations/work-orders/work-order-detail/public-work-order-state.ts'
import { PublicWorkOrderView } from '#components/domain/operations/work-orders/work-order-detail/public-work-order-view.tsx'
import { hasAuthenticatedSession, resolveCompanySlug } from '#core/auth/public-session.ts'
import { useComponentState } from '#core/state/State.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '#i18n.ts'

/**
 * Public, unauthenticated view of a single work order shared via the app's ShareLink
 * mechanism. The URL carries the company + work-order ids in the path and the raw
 * `share_token` as `?token=`; {@link PublicWorkOrderState} exchanges that token for a
 * short-lived JWT and loads the work order as that share identity.
 */
export const Route = createFileRoute('/_public/share/work-order/$companyId/$workOrderId')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search['token'] as string | undefined) ?? undefined,
  }),
  // Preload the namespace so t() has translations before the component renders (prevents raw-key flash).
  loader: () => i18n.loadNamespaces('work-orders'),
  component: PublicWorkOrderPage,
})

function PublicWorkOrderPage() {
  const { t, ready } = useTranslation('work-orders')
  const { companyId, workOrderId } = Route.useParams()
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const state = useComponentState(PublicWorkOrderState)

  // A logged-in user must not go through the anonymous share flow (it would inject a
  // share token and overwrite their session in this tab). Send them into the in-app
  // work-order view, routed on the company slug so the URL matches the rest of the app.
  const isAuthenticated = hasAuthenticatedSession()
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({
        to: '/$companyId/work-orders/$workOrderId',
        params: { companyId: resolveCompanySlug(companyId), workOrderId },
        replace: true,
      })
      return
    }
    if (!token) {
      state.reactTo.error = true
      state.reactTo.loading = false
      return
    }
    void state.load(companyId, workOrderId, token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Guard against a raw-key flash while the namespace resolves.
  if (!ready || isAuthenticated) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        {isAuthenticated ? t('shared.fields.redirecting') : null}
      </div>
    )
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-9 col-lg-7">
          <p className="text-secondary mb-3">
            {state.canCompleteSteps ? t('public.completeStepsNote') : t('public.readOnlyNote')}
          </p>
          {state.loading ? (
            <div className="text-center text-secondary py-5">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t('public.loading')}
            </div>
          ) : state.error || !state.workOrder ? (
            <div className="alert alert-danger" role="alert">
              {t('public.invalid')}
            </div>
          ) : (
            <PublicWorkOrderView
              workOrder={state.workOrder}
              readOnly={!state.canCompleteSteps}
              onSaveResponse={(stepId, raw, fieldType) => void state.saveStepResponse(stepId, raw, fieldType)}
              savingStepIds={state.savingStepIds}
            />
          )}
        </div>
      </div>
    </div>
  )
}
